// admin/src/lib/reorder.ts
//
// Shared drag-to-reorder helpers for the equipment multi-select, the
// video list and anything else with a `sort` column. `reorder` is plain
// array move math (used by whatever drag library the UI picks); `withSort`
// turns the resulting order into the `{ id, sort }` pairs to write back
// (equipment.sort, exercise_equipment.sort, exercise_media.sort are all
// `smallint`/`int`, 0-based, dense).

/** Moves the item at `from` to `to`, returning a NEW array (does not mutate `list`). */
export function reorder<T>(list: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || from >= list.length || to < 0 || to >= list.length) {
    return [...list];
  }
  const copy = [...list];
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
}

/** Assigns a dense, 0-based `sort` to each item in its current array order. */
export function withSort<T>(list: readonly T[]): Array<T & { sort: number }> {
  return list.map((item, index) => ({ ...item, sort: index }));
}

/** Pulls just the `{ id, sort }` pairs out of a reordered list, for a minimal batch update. */
export function sortAssignments<T extends { id: string }>(list: readonly T[]): Array<{ id: string; sort: number }> {
  return list.map((item, index) => ({ id: item.id, sort: index }));
}
